import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, ArrowRight, Star, ExternalLink } from 'lucide-react'
import { researchPapers } from '../data/algorithms'

const filters = [
  { value: 'all', label: 'All Papers' },
  { value: 'nlp', label: 'NLP' },
  { value: 'reinforcement-learning', label: 'Reinforcement Learning' },
  { value: 'finance', label: 'Finance' },
  { value: 'graph-learning', label: 'Graph Learning' },
]

export default function ResearchPapers() {
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')

  const filtered = researchPapers.filter((algo) => {
    const matchesSearch =
      searchTerm === '' ||
      algo.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      algo.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesFilter = activeFilter === 'all' || algo.category === activeFilter
    return matchesSearch && matchesFilter
  })

  return (
    <div className="pt-24">
      {/* Hero — Asymmetric, Editorial */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-copper/3 pointer-events-none" />
        <div className="section-base relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="max-w-3xl"
          >
            <span className="eyebrow block mb-6">The Archive</span>
            <h1 className="headline-xl text-cream mb-8">
              Research Papers, <span className="text-copper">Implemented</span>
            </h1>
            <p className="body-large max-w-2xl">
              We take seminal research papers and turn them into production-ready, well-documented code.
              Every implementation includes architecture breakdowns, training results, and deployment guides.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filters — Minimal */}
      <section className="pb-10">
        <div className="section-base">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mb-8"
          >
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-cream-dark" />
              <input
                type="text"
                placeholder="Search algorithms by name or tag..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-4 bg-ink-card border border-ink-border rounded text-cream placeholder:text-cream-dark focus:outline-none focus:border-copper/50 focus:ring-1 focus:ring-copper/20 transition-all text-sm"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-2 mb-8"
          >
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setActiveFilter(f.value)}
                className={`px-4 py-2 rounded text-xs font-mono uppercase tracking-wider transition-all duration-300 ${
                  activeFilter === f.value
                    ? 'bg-copper text-ink'
                    : 'bg-ink-card border border-ink-border text-cream-dark hover:border-cream-dark/50 hover:text-cream'
                }`}
              >
                {f.label}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Algorithm List — Editorial, No Cards */}
      <section className="pb-24">
        <div className="section-base">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter + searchTerm}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="divide-y divide-ink-border"
            >
              {filtered.map((algo, i) => (
                <motion.div
                  key={algo.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  <Link
                    to={`/algorithm/${algo.id}`}
                    className="group block py-8 lg:py-10 transition-colors duration-300"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-8">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          {algo.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="text-xs font-mono text-cream-dark uppercase tracking-wider">
                              {tag}
                            </span>
                          ))}
                          <span className={`text-xs font-mono ${
                            algo.difficulty === 'Expert' ? 'text-red-400/70' :
                            algo.difficulty === 'Advanced' ? 'text-yellow-400/70' :
                            'text-green-400/70'
                          }`}>
                            {algo.difficulty}
                          </span>
                        </div>
                        <h3 className="headline-sm text-cream group-hover:text-copper transition-colors duration-300 mb-2">
                          {algo.title}
                        </h3>
                        <p className="body-text max-w-2xl line-clamp-2">
                          {algo.abstract}
                        </p>
                        <p className="text-xs font-mono text-cream-dark mt-3 tracking-wide">
                          {algo.authors.join(', ')} — {algo.venue}
                        </p>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-cream-dark shrink-0 lg:pt-1">
                        <span className="flex items-center gap-1.5">
                          <Star size={14} className="text-copper" />
                          {algo.stars.toLocaleString()}
                        </span>
                        <span className="font-mono text-xs">{algo.language}</span>
                        <ExternalLink size={14} className="text-cream-dark group-hover:text-copper transition-colors" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {filtered.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Search size={48} className="mx-auto text-cream-dark mb-4" />
              <h3 className="headline-sm text-cream mb-2">No algorithms found</h3>
              <p className="body-text">Try adjusting your search or filters.</p>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  )
}
